import * as R from 'ramda';
// constants
import * as GC from '../constants';
// helpers
import { getAmousConfigByNameFromWindow } from './window';
import { ifElse, isNumber, isNotZero, isFunction } from './';
import { weightToKgs, fromKgsToPounds, mathRoundNumber } from './calc';
//////////////////////////////////////////////////

const mapSearchedItem = R.curry((item) => R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR, item));

const getDimensionInFeet = (dimension, uom) => {
  const map = {
    [GC.UOM_FOOT]: () => dimension,
    [GC.UOM_INCH]: () => R.divide(dimension, 12),
    [GC.UOM_METER]: () => R.multiply(dimension, 3.281),
    [GC.UOM_CENTIMETER]: () => R.divide(dimension, 30.48),
  };

  return map[uom]();
};

const getDimensionInInches = (dimension, uom) => {
  const map = {
    [GC.UOM_INCH]: () => dimension,
    [GC.UOM_FOOT]: () => R.multiply(dimension, 12),
    [GC.UOM_METER]: () => R.multiply(dimension, 39.3701),
    [GC.UOM_CENTIMETER]: () => R.divide(dimension, 2.54),
  };

  return map[uom]();
};

const getDimensionInMeters = (dimension, uom) => {
  const map = {
    [GC.UOM_METER]: () => dimension,
    [GC.UOM_FOOT]: () => R.divide(dimension, 3.281),
    [GC.UOM_INCH]: () => R.multiply(dimension, 0.0254),
    [GC.UOM_CENTIMETER]: () => R.divide(dimension, 100),
  };

  return map[uom]();
};

const getDimensionInCentimeters = (dimension, uom) => {
  const map = {
    [GC.UOM_CENTIMETER]: () => dimension,
    [GC.UOM_INCH]: () => R.multiply(dimension, 2.54),
    [GC.UOM_METER]: () => R.multiply(dimension, 100),
    [GC.UOM_FOOT]: () => R.divide(dimension, 0.03281),
  };

  return map[uom]();
};

const convertDimensionValueByUoms = (value, fromUom, toUom) => {
  if (R.equals(toUom, GC.UOM_FOOT)) return getDimensionInFeet(value, fromUom);

  if (R.equals(toUom, GC.UOM_INCH)) return getDimensionInInches(value, fromUom);

  if (R.equals(toUom, GC.UOM_METER)) return getDimensionInMeters(value, fromUom);

  if (R.equals(toUom, GC.UOM_CENTIMETER)) return getDimensionInCentimeters(value, fromUom);
};

const getWeightInLbs = (weight, uom) => {
  const map = {
    [GC.UOM_POUND]: () => weight,
    [GC.UOM_KILOGRAM]: () => fromKgsToPounds(weight),
    [GC.UOM_TON_US]: () => fromKgsToPounds(weightToKgs({ from: GC.UOM_TON_US, value: weight })),
    [GC.UOM_METRIC_TON]: () => fromKgsToPounds(weightToKgs({ from: GC.UOM_METRIC_TON, value: weight })),
  };

  return map[uom]();
};

const calculateFreightClass = (data) => {
  const {
    weight,
    quantity,
    weightType,
    dimensionsW,
    dimensionsL,
    dimensionsH,
    dimensionsUOM,
  } = data;

  const freightClassCalculator = getAmousConfigByNameFromWindow(GC.CLO_ITEM_FREIGHT_CLASS_CALCULATOR);

  const width = getDimensionInFeet(dimensionsW, dimensionsUOM);
  const length = getDimensionInFeet(dimensionsL, dimensionsUOM);
  const height = getDimensionInFeet(dimensionsH, dimensionsUOM);

  const volume = width * length * height * quantity;

  const weightToUse = getWeightInLbs(weight, weightType);

  const lbsPerCubicFeet = R.divide(weightToUse, volume);

  let value = '';

  if (R.equals(freightClassCalculator, GC.FREIGHT_CLASS_CALCULATOR_STANDARD)) {
    switch (true) {
      case R.gte(lbsPerCubicFeet, 50):
        value = 'FIFTY';
        break;
      case R.gte(lbsPerCubicFeet, 35):
        value = 'FIFTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 30):
        value = 'SIXTY';
        break;
      case R.gte(lbsPerCubicFeet, 22.5):
        value = 'SIXTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 15):
        value = 'SEVENTY';
        break;
      case R.gte(lbsPerCubicFeet, 13.5):
        value = 'SEVENTY_SEVEN_POINT_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 12):
        value = 'EIGHTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 10.5):
        value = 'NINETY_TWO_POINT_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 9):
        value = 'ONE_HUNDRED';
        break;
      case R.gte(lbsPerCubicFeet, 8):
        value = 'ONE_HUNDRED_TEN';
        break;
      case R.gte(lbsPerCubicFeet, 7):
        value = 'ONE_HUNDRED_TWENTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 6):
        value = 'ONE_HUNDRED_FIFTY';
        break;
      case R.gte(lbsPerCubicFeet, 5):
        value = 'ONE_HUNDRED_SEVENTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 4):
        value = 'TWO_HUNDRED';
        break;
      case R.gte(lbsPerCubicFeet, 3):
        value = 'TWO_HUNDRED_FIFTY';
        break;
      case R.gte(lbsPerCubicFeet, 2):
        value = 'THREE_HUNDRED';
        break;
      case R.gte(lbsPerCubicFeet, 1):
        value = 'FOUR_HUNDRED';
        break;
      case R.lt(lbsPerCubicFeet, 1):
        value = 'FIVE_HUNDRED';
        break;
      default:
        value = '';
    }
  } else if (R.equals(freightClassCalculator, GC.FREIGHT_CLASS_CALCULATOR_FAK)) {
    switch (true) {
      case R.gte(lbsPerCubicFeet, 30):
        value = 'SIXTY';
        break;
      case R.gte(lbsPerCubicFeet, 22.5):
        value = 'SIXTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 15):
        value = 'SEVENTY';
        break;
      case R.gte(lbsPerCubicFeet, 12):
        value = 'EIGHTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 10):
        value = 'NINETY_TWO_POINT_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 8):
        value = 'ONE_HUNDRED';
        break;
      case R.gte(lbsPerCubicFeet, 6):
        value = 'ONE_HUNDRED_TWENTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 4):
        value = 'ONE_HUNDRED_SEVENTY_FIVE';
        break;
      case R.gte(lbsPerCubicFeet, 2):
        value = 'TWO_HUNDRED_FIFTY';
        break;
      case R.gte(lbsPerCubicFeet, 1):
        value = 'THREE_HUNDRED';
        break;
      case R.gte(lbsPerCubicFeet, 0):
        value = 'FOUR_HUNDRED';
        break;
      default:
        value = '';
    }
  }

  return value;
};

const convertToFeetOrMeterByConfig = ({ unit, value, config }) => {
  const map = {
    [GC.IMPERIAL_SYSTEM]: {
      [GC.UOM_FOOT]: () => value,
      [GC.UOM_INCH]: () => value / 12,
      [GC.UOM_METER]: () => value * 3.28084,
      [GC.UOM_CENTIMETER]: () => value / 30.48,
    },
    [GC.METRIC_SYSTEM]: {
      [GC.UOM_METER]: () => value,
      [GC.UOM_FOOT]: () => value * 0.3048,
      [GC.UOM_INCH]: () => value * 0.0254,
      [GC.UOM_CENTIMETER]: () => value / 100,
    },
  };

  const functionToCall = R.path([config, unit], map);

  return isFunction(functionToCall) ? functionToCall() : 1;
};

const convertWeightToPoundsOrKilogramsByConfig = ({ unit, value, config }) => {
  const map = {
    [GC.IMPERIAL_SYSTEM]: {
      [GC.UOM_POUND]: () => value,
      [GC.UOM_TON_US]: () => value * 2000,
      [GC.UOM_KILOGRAM]: () => value * 2.20462,
      [GC.UOM_METRIC_TON]: () => value * 2204.62,
    },
    [GC.METRIC_SYSTEM]: {
      [GC.UOM_KILOGRAM]: () => value,
      [GC.UOM_TON_US]: () => value * 907.185,
      [GC.UOM_POUND]: () => value * 0.453592,
      [GC.UOM_METRIC_TON]: () => value * 1000,
    },
  };

  const functionToCall = R.path([config, unit], map);

  return isFunction(functionToCall) ? functionToCall() : 1;
};

const calcItemCubicVolume = ({
  unit,
  width,
  length,
  height,
  quantity = 1,
  config = GC.IMPERIAL_SYSTEM,
}) => {
  const w = convertToFeetOrMeterByConfig({ unit, config, value: width });
  const l = convertToFeetOrMeterByConfig({ unit, config, value: length });
  const h = convertToFeetOrMeterByConfig({ unit, config, value: height });

  const volume = l * w * h * quantity;
  const rounded = mathRoundNumber(volume);

  return R.equals(rounded, 0) ? volume : rounded;
};

const getItemCubicCapacityValues = (item) => {
  const {
    weight,
    config,
    quantity,
    weightType,
    dimensionsW,
    dimensionsL,
    dimensionsH,
    dimensionsUOM,
  } = item;

  const cubicVolume = calcItemCubicVolume({
    config,
    quantity,
    width: dimensionsW,
    length: dimensionsL,
    height: dimensionsH,
    unit: dimensionsUOM,
  });

  const weightByConfig = convertWeightToPoundsOrKilogramsByConfig({
    config,
    value: weight,
    unit: weightType,
  });

  let cubicDensity = 0;

  if (isNumber(cubicVolume) && isNotZero(cubicVolume)) {
    const densityNotRounded = weightByConfig / cubicVolume;
    const densityRounded = mathRoundNumber(densityNotRounded);

    cubicDensity = R.equals(densityRounded, 0) ? densityNotRounded : densityRounded;
  }

  return { cubicVolume, cubicDensity, weight: weightByConfig };
};

const getTotalCubicCapacityValuesFromEvents = ({ items, config }) => {
  const { totalWeight, totalCubicVolume } = R.reduce(
    (acc, items) => {
      const { itemTotalWeight, itemTotalCubicVolume } = R.reduce(
        (itemAcc, item) => {
          const { weight, cubicVolume } = getItemCubicCapacityValues({ ...item, config });

          return {
            itemTotalWeight: R.add(itemAcc.itemTotalWeight, weight),
            itemTotalCubicVolume: R.add(itemAcc.itemTotalCubicVolume, cubicVolume),
          };
        },
        { itemTotalWeight: 0, itemTotalCubicVolume: 0 },
        items,
      );

      return {
        totalWeight: R.add(acc.totalWeight, itemTotalWeight),
        totalCubicVolume: R.add(acc.totalCubicVolume, itemTotalCubicVolume),
      };
    },
    { totalWeight: 0, totalCubicVolume: 0 },
    items,
  );

  const densityNotRounded = totalWeight / totalCubicVolume;
  const totalDensityRounded = mathRoundNumber(densityNotRounded);
  const totalDensity = R.equals(totalDensityRounded, 0) ? densityNotRounded : totalDensityRounded;

  return {
    totalWeight,
    totalDensity,
    totalCubicVolume,
  };
};

const checkCubicCapacityRules = ({ config, totalCubicVolume, totalDensity }) => {
  const isImperial = R.equals(config, GC.IMPERIAL_SYSTEM);
  const min = ifElse(isImperial, 350, 9.91);
  const max = ifElse(isImperial, 750, 21.24);
  const minDensity = ifElse(isImperial, 3, 48);
  const maxDensity = ifElse(isImperial, 6, 96);

  const rule1 =
    totalCubicVolume > min &&
    totalCubicVolume <= max &&
    totalDensity < minDensity;

  const rule2 =
    totalCubicVolume > max &&
    totalDensity < maxDensity;

  return rule1 || rule2;
};

export {
  mapSearchedItem,
  calculateFreightClass,
  checkCubicCapacityRules,
  getItemCubicCapacityValues,
  convertDimensionValueByUoms,
  getTotalCubicCapacityValuesFromEvents,
};
